import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getPaperTradingSessions } from '../../api';
import StartPaperTradingModal from '../../components/StartPaperTradingModal';
import Button from '../../components/Button';

export default function PaperTradingCTA({ backtest, onRequiresSignup, onPaperTradeStarted }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [existingPaperSession, setExistingPaperSession] = useState(null);

  // Check if this backtest is already being paper-traded (by backtest ID or same strategy+symbol)
  useEffect(() => {
    if (!isAuthenticated || !backtest?.id || backtest.status !== 'completed') return;
    getPaperTradingSessions()
      .then(({ data }) => {
        const strategyId = backtest.strategy || backtest.strategy_id;
        const active = data.find((s) =>
          (s.status === 'active' || s.status === 'paused') && (
            s.backtest === backtest.id ||
            (strategyId && s.strategy === strategyId && s.symbol === backtest.symbol)
          )
        );
        setExistingPaperSession(active || null);
      })
      .catch(() => {});
  }, [isAuthenticated, backtest?.id, backtest?.status, backtest?.strategy, backtest?.strategy_id, backtest?.symbol]);

  if (!isAuthenticated || backtest.status !== 'completed') return null;

  // Lead with the actual backtest result when it's a winner — a concrete number is
  // far more enticing than "simulate live trading". Only showcase positive returns;
  // for flat/negative ones we lean on the forward-test + alerts hook instead.
  const results = backtest.results || {};
  const returnPct = results.return_pct;
  const hasReturn = typeof returnPct === 'number' && Number.isFinite(returnPct);
  const returnStr = hasReturn ? `${returnPct >= 0 ? '+' : ''}${returnPct}%` : null;
  const showcaseReturn = hasReturn && returnPct > 0;

  return (
    <>
      {showModal && (
        <StartPaperTradingModal
          backtest={backtest}
          onStarted={(session) => {
            setShowModal(false);
            if (onPaperTradeStarted) onPaperTradeStarted(session);
          }}
          onClose={() => setShowModal(false)}
        />
      )}
      <div style={{
        background: existingPaperSession
          ? 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(47,214,195,0.08))'
          : 'linear-gradient(135deg, rgba(47,214,195,0.1), rgba(99,102,241,0.1))',
        border: `1px solid ${existingPaperSession ? 'rgba(99,102,241,0.35)' : 'rgba(47,214,195,0.3)'}`,
        borderRadius: 10, padding: '16px 20px', marginBottom: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 220, flex: 1 }}>
          <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            {existingPaperSession
              ? '📊 This strategy is trading live'
              : (showcaseReturn
                  ? `🚀 It made ${returnStr} in backtest — now watch it trade live`
                  : '🚀 Watch this strategy trade live')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            {existingPaperSession
              ? `Session "${existingPaperSession.name}" is ${existingPaperSession.status}. Check in on how it's doing.`
              : "Put it on live market data with virtual capital — we'll email you the moment it opens or closes a position, so you never have to check back."}
          </div>
          {!existingPaperSession && (
            <div style={{
              fontSize: 12, color: 'var(--muted)', marginTop: 8,
              display: 'flex', gap: 14, flexWrap: 'wrap', opacity: 0.9,
            }}>
              <span>💸 Virtual capital · zero risk</span>
              <span>🔔 Trade alerts by email</span>
              <span>✓ Free</span>
            </div>
          )}
        </div>
        {existingPaperSession ? (
          <Button
            variant="ghost"
            onClick={() => navigate(`/paper-trading/${existingPaperSession.id}`)}
            style={{ borderColor: 'var(--teal)', flexShrink: 0 }}
          >
            View Dashboard →
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              if (!isAuthenticated) { onRequiresSignup && onRequiresSignup('Sign up to start paper trading.'); return; }
              setShowModal(true);
            }}
            style={{ flexShrink: 0 }}
          >
            Trade it live — free →
          </Button>
        )}
      </div>
    </>
  );
}
